import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { AssessmentGraphicsResultsService } from '@qaroni-app/assessment/services/assessment-results/assessment-graphics-results.service';
import { QaroniHtmlLegendPlugin } from '@qaroni-shared/qaroni-plugins-charts/qaroni-html-legend-plugin';
import { AssessmentCompetencyGraphicResult } from '@qaroni-shared/types/assessment-competency-graphic-result';
import { Chart, ChartDataset, ChartOptions, registerables } from 'chart.js';

@Component({
  selector: 'qaroni-result-graphics-canvas',
  templateUrl: './result-graphics-canvas.component.html',
  styleUrl: './result-graphics-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResultGraphicsCanvasComponent
  extends QarepaBaseSubsDirective
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly assessmentID = input<number>(0);
  readonly companyID = input<number>(0);
  readonly employeeID = input<number>(0);
  readonly evaluationID = input<number>(0);
  readonly evaluatedID = input<number>(0);

  @ViewChild('legendContainer') legendContainer!: ElementRef<HTMLElement>;
  @ViewChild('radarChart') radarChart!: ElementRef<HTMLCanvasElement>;

  private assessmentGraphicsResultsService = inject(
    AssessmentGraphicsResultsService
  );

  private results$ =
    this.assessmentGraphicsResultsService.getAssessmentTypeCompetenciesResultsSubject$();

  private chart: Chart | null = null;
  private radarChartCanvas: HTMLCanvasElement | null = null;
  private data: AssessmentCompetencyGraphicResult[] = [];

  constructor() {
    super();
    Chart.register(...registerables);
  }

  ngOnInit() {
    this.subs.add(this.results$.subscribe(this.getDataResults));

    const employeeID = this.employeeID();
    if (!employeeID) {
      return;
    }

    const companyID = this.companyID();
    const assessmentID = this.assessmentID();
    const evaluationID = this.evaluationID();
    const evaluatedID = this.evaluatedID();
    if (companyID && assessmentID) {
      this.assessmentGraphicsResultsService.assessmentTypeCompetenciesResults(
        companyID,
        assessmentID,
        employeeID
      );
    } else if (evaluationID && evaluatedID) {
      this.assessmentGraphicsResultsService.responsableEmployeeEvaluationTypeResults(
        employeeID,
        evaluationID,
        evaluatedID
      );
    }
  }

  ngAfterViewInit() {
    this.radarChartCanvas = this.radarChart.nativeElement;
    this.radarChartSetup();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private getDataResults = (data: AssessmentCompetencyGraphicResult[]) => {
    if (!data) {
      return;
    }
    this.data = data;
    this.radarChartSetup();
  };

  private radarChartSetup() {
    if (this.chart) {
      this.chart.destroy();
    }

    const skills = this.data.map(item => item.skill);
    const autoevaluationScores = this.data.map(
      item => item.autoEvaluationScore ?? 0
    );
    const responsableScores = this.data.map(item => item.responsableScore ?? 0);
    const peerScores = this.data.map(item => item.peerScore ?? 0);

    const data = this.dataCanvas(
      skills,
      autoevaluationScores,
      responsableScores,
      peerScores
    );

    const options: ChartOptions<'radar'> = {
      responsive: true,
      maintainAspectRatio: false,
      onResize(chart, size) {
        if (size.width < 500) {
          chart.options.scales.r.ticks.display = false;
        } else {
          chart.options.scales.r.ticks.display = true;
        }
      },
      elements: {
        point: {
          radius: 0,
        },
        line: {
          borderWidth: 2,
        },
      },
      scales: {
        r: {
          min: 0,
          max: 100,
          ticks: {
            color: '#000000',
            stepSize: 20,
          },
          grid: {
            circular: true,
          },
          beginAtZero: true,
        },
      },
      plugins: {
        datalabels: {
          formatter: (value: number) => {
            void value;
            return '';
          },
        },
        legend: {
          display: false,
        },
      },
    };

    this.chart = new Chart(this.radarChartCanvas, {
      type: 'radar',
      data: data,
      options: options,
      plugins: [new QaroniHtmlLegendPlugin(this.legendContainer, 'line')],
    });
  }

  private dataCanvas(
    skills: string[],
    autoevaluationScores: number[],
    responsableScores: number[],
    peerScores: number[]
  ) {
    const datasets: ChartDataset[] = [];

    const employeeID = this.employeeID();
    if (employeeID && this.companyID() && this.assessmentID()) {
      if (!peerScores.every(score => score === 0)) {
        datasets.push({
          label: 'Evaluación Par',
          data: peerScores,
          fill: false,
          backgroundColor: '#3C98D4',
          borderColor: '#3C98D4',
        });
      }

      if (!autoevaluationScores.every(score => score === 0)) {
        datasets.push({
          label: 'Autoevaluación',
          data: autoevaluationScores,
          fill: false,
          backgroundColor: '#ED985F',
          borderColor: '#ED985F',
        });
      }

      if (!responsableScores.every(score => score === 0)) {
        datasets.push({
          label: 'Evaluación Principal',
          data: responsableScores,
          fill: false,
          backgroundColor: '#66a257',
          borderColor: '#66a257',
        });
      }
    } else if (employeeID && this.evaluationID() && this.evaluatedID()) {
      datasets.push(
        {
          label: 'Autoevaluación',
          data: autoevaluationScores,
          fill: false,
          backgroundColor: '#ED985F',
          borderColor: '#ED985F',
        },
        {
          label: 'Evaluación Par',
          data: peerScores,
          fill: false,
          backgroundColor: '#3C98D4',
          borderColor: '#3C98D4',
        },
        {
          label: 'Evaluación Principal',
          data: responsableScores,
          fill: false,
          backgroundColor: '#66a257',
          borderColor: '#66a257',
        }
      );
    }

    return {
      labels: skills,
      datasets: datasets,
    };
  }
}
